/**
 * <p>Definiert die Schnittstelle für die Kommunikation mit dem WBT-Player. Die Nachrichten können über die Methode
 * frame.postMessage(message, targetOrigin) gesendet werden. Mit window.addEventListener("message", receiveMessageFunction, false)
 * können die Nachrichten empfangen werden.<br>
 * Es werden nur Nachrichten verarbeitet, die vom registrierten Frame kommen.</p>
 *
 * <h5>Nachrichten</h5>
 * <ul>
 *  <li>initInterface: Aktiviert die Schnittstelle</li>
 *  <li>pageStatus (RW): Status der Seite, bei Infoseiten "completed" oder "incomplete", bei Aufgabenseiten "failed" oder "success" statt "completed". Geschrieben werden kann "completed" oder "incomplete".</li>
 *  <li>pageScore (RW): Punkte der Seite, 100 Punkte zum Bestehen. Nur bei Aufgabenseiten definiert.</li>
 *  <li>learnerName (R): Name des Lernenden ("Vorname Nachname"). Wenn sich der Name im Nachhinein ändert, wird er erneut gesendet.</li>
 *  <li>learnerId (R): Nutzer-ID des Lernenden. Wenn sich die ID im Nachhinein ändert, wird sie erneut gesendet.</li>
 * </ul>
 * @example <caption>XML-Fragment</caption>
 * <interface width="750" height="500" src="./content/annex/interface/index.html" id="XML01" left="235" top="85"/>
 *
 * @example <caption>Helferfunktion</caption>
 * function postMessage(ressource, params, error) {
 *      var msg = JSON.stringify({ ressource: ressource, params: params, error: error });
 *      parent.postMessage(msg, "*");
 * }
 *
 * @example <caption>Initialisieren</caption>
 * window.addEventListener("message", receiveMessage, false);
 * postMessage("initInterface", null, null);
 *
 * @example <caption>Daten abfragen</caption>
 * postMessage("learnerName", null, null);
 * postMessage("learnerId", null, null);
 * postMessage("pageStatus", null, null);
 * postMessage("pageScore", null, null);
 *
 * @example <caption>Werte setzen</caption>
 * postMessage("pageStatus", {status: "incomplete"}, null);
 * postMessage("pageStatus", {status: "completed"}, null);
 * postMessage("pageScore", {score: 50}, null);
 * postMessage("pageScore", {score: 100}, null);
 *
 * @class service_v3sInterfaceService
 * @memberOf v3sWbtPlayerApp:page:elements
 */
angular.module('v3sWbtPlayerApp').factory('v3sInterfaceService', ["$rootScope", "$window", "v3sLog", "v3sNavigationService", "v3sUserService", function ($rootScope, $window, v3sLog, v3sNavigationService, v3sUserService) {

    var targetFrame = null;
    var unregisterScope = null;
    var learnerNamePending = false;
    var learnerIdPending = false;

    $window.addEventListener("message", receiveMessage, false);

    /**
    * Schickt die Nachricht an das Zielfenster
    * @memberOf v3sWbtPlayerApp:page:elements.service_v3sInterfaceService
    * @param {string} ressource - Ressource [pageStatus,pageScore,learnerName,learnerId]
    * @param {Object} result - Ergebnis der Anfrage
    * @param {string} error - Fehlerbeschreibung
    */
    function postResult(ressource, result, error) {
        if (targetFrame) {
            result = result ? result : null;
            error = error ? error : null;
            var msg = angular.toJson({ ressource: ressource, result: result, error: error }, false);
            targetFrame.postMessage(msg, "*");
        }
    }

    /**
    * Empfängt die Nachricht vom Zielfenster.
    * @memberOf v3sWbtPlayerApp:page:elements.service_v3sInterfaceService
    * @param {Object} event - Nachricht
    */
    function receiveMessage(event) {
        var result = "";
        try {
            if (event.source && event.data) {
                var data = angular.fromJson(event.data);
                if (data && data.ressource && !data.result && !data.error) {
                    if (data.ressource === "initInterface") {
                        result = init(event.source);

                    } else if (targetFrame && targetFrame === event.source) {
                        switch (data.ressource) {
                            case "pageStatus":
                                result = pageStatus(data.params);
                                break;
                            case "pageScore":
                                result = pageScore(data.params);
                                break;
                            case "learnerName":
                                result = learnerName();
                                break;
                            case "learnerId":
                                result = learnerId();
                                break;

                            default:
                                postResult(data.ressource, null, "Error: Unknown ressource: " + data.ressource);
                        }
                    }

                    if (result) {
                        postResult(data.ressource, result, null);
                    }
                }
            }

        } catch (e) {
            v3sLog.warn("Exception: ", false, e);
        }
    }

    function pageStatus(params) {
        var status = "";
        var page = v3sNavigationService.getCurrentPage();
        if (page) {
            if (params && params.status) {
                page.setStatus(params.status, { overwrite: true });
            }

            if (page.isCompleted() && page.isExercise()) {
                status = page.getExerciseStatus();
            } else {
                status = page.getStatus();
            }
        }

        return { status: status };
    }

    function pageScore(params) {
        var score = 0;
        var page = v3sNavigationService.getCurrentPage();
        if (page) {
            if (params) {
                page.setScore(params.score, { overwrite: true });
            }

            score = page.getScore();
        }

        return { score: score };
    }

    function learnerName() {
        var forename = v3sUserService.get("forename");
        var surname = v3sUserService.get("surname");
        learnerNamePending = true;

        return { name: (forename + " " + surname).trim() };
    }

    function learnerId() {
        learnerIdPending = true;

        return { id: v3sUserService.get("id") }
    }

    /**
    * Initialisiert die Schnittstelle
    * @memberOf v3sWbtPlayerApp:page:elements.service_v3sInterfaceService
    * @param {Object} newFrame - Objekt, über welches die Nachrichten gesendet und empfangen werden
    */
    function init(newFrame) {
        return { inited: targetFrame == newFrame };
    }

    function onUserChanged() {
        var result;
        if (learnerIdPending) {
            postResult("learnerId", learnerId(), null);
        }

        if (learnerNamePending) {
            postResult("learnerName", learnerName(), null);
        }
    }

    function registerUserChanged() {
        unregisterScope = $rootScope.$on('v3sUserChanged', onUserChanged);
    }

    /**
    * Setzt das Zielfenster.
    * @memberOf v3sWbtPlayerApp:page:elements.service_v3sInterfaceService
    * @param {Object} newFrame - Objekt, über welches die Nachrichten gesendet und empfangen werden
    */
    function setTargetFrame(newFrame) {
        targetFrame = newFrame;
        learnerNamePending = false;
        learnerIdPending = false;

        if (!newFrame && unregisterScope) {
            unregisterScope();
            unregisterScope = null;
        } else if (newFrame && !unregisterScope) {
            registerUserChanged();
        }
    }

    return {
        setTargetFrame: setTargetFrame
    }
}]);
